import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { task } from 'ember-concurrency'
import type ManageProgressRoute from 'teacher-ui-client/manage/progress/route'
import {
  serialize as serializeSelected,
  deserialize as deserializeSelected,
} from '@blakeelearning/data-tables/utils/selecting'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import { hasId } from 'teacher-ui-client/utils/type-guards'

export default class ManageProgressController extends Controller {
  @service studentProgress!: StudentProgressService

  declare model: ModelFor<ManageProgressRoute>

  queryParams = ['sort', 'pageNumber', 'perPage', 'textFilter', 'selected', 'picked', 'product']

  @tracked sort = 'firstName'

  @tracked pageNumber = 1

  @tracked perPage = 10

  @tracked textFilter = ''

  @tracked selected = ''

  @tracked picked = ''

  @tracked product = ''

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  get paginationConfig(): PaginationConfig {
    return { pageNumber: Number(this.pageNumber), perPage: Number(this.perPage) }
  }

  get selectedStudentIds() {
    return deserializeSelected(this.selected, String)
  }

  set selectedStudentIds(newSelectedIds) {
    this.selected = serializeSelected(newSelectedIds)
  }

  get pickedItemId() {
    const isDigits = (str: string) => /^\d+$/.test(str) // ensure string is a positive integer (and checks for empty str '')
    return isDigits(this.picked) ? this.picked : ''
  }

  set pickedItemId(value) {
    this.picked = value
  }

  @tracked editProgressModalOpen = false

  get students() {
    return this.model.allocatedStudents.filter(hasId)
  }

  get selectedStudents() {
    const selectedIds = this.selectedStudentIds
    return this.students.filter((student) => selectedIds.includes(student.id))
  }

  get pickedStudent() {
    const pickedId = this.pickedItemId
    return this.students.find((student) => pickedId === student.id)
  }

  /**
   * Call this with the student progresses that were saved, this will map them to contexts for updating
   */
  updateProgressesTask = task({ drop: true }, async (studentProgresses: Record<PropertyKey, unknown>[]) => {
    await this.studentProgress.loadProgresses(
      studentProgresses.map((sp) => ({
        product: sp.product,
        precinct: sp.precinct,
        remoteIds: sp.student_ids,
        curriculum: sp.curriculum, // msp needs this field
      })),
    )
  })

  @action
  editProgressModal(value: boolean) {
    this.editProgressModalOpen = value
  }

  @action
  onSuccess(studentProgresses: Record<PropertyKey, unknown>[] = []) {
    this.editProgressModalOpen = false

    this.selectedStudentIds = []
    this.pickedItemId = ''
    // go update progresses cache for those students that were edited
    void this.updateProgressesTask.perform(studentProgresses)
  }

  @action
  onCancel() {
    this.editProgressModalOpen = false
    this.pickedItemId = ''
  }

  @action
  setTextFilter(event: Event & { target: HTMLInputElement }) {
    this.textFilter = event.target.value
    this.pageNumber = 1
  }

  /** Select student id based on the item provided */
  @action
  itemSelectAction({ id }: { id: string }) {
    const { selectedStudentIds } = this
    const isSelected = selectedStudentIds.includes(id)
    if (isSelected) {
      this.selectedStudentIds = selectedStudentIds.filter((selectedId) => selectedId !== id)
    } else {
      this.selectedStudentIds = [...selectedStudentIds, id]
    }
  }

  /** Select exactly the students provided */
  @action
  itemSelectAllAction(items: { id: string }[]) {
    const itemIds = items.map((item) => item.id)
    this.selectedStudentIds = itemIds
  }

  // Pick the single student for editing
  @action
  itemPickAction({ id }: { id: string }) {
    this.pickedItemId = id
    this.editProgressModalOpen = true
  }

  /**
   * This action should be called when a column is clicked, it sets the new sortingConfig for the URL query params
   */
  @action
  updateSorts(sortObject: SortObject) {
    if (sortObject.sortKey) {
      const newSortConfig = updateSortColumns(this.sortingConfig, String(sortObject.sortKey))
      this.sortingConfig = newSortConfig
    }
  }
}

declare module '@ember/controller' {
  interface Registry {
    'manage/progress': ManageProgressController
  }
}
